import BasicTable from "@/components/shared/BasicTable";
import { BasicTableRef, IntegrationProductVo } from "@/types";
import { ColDef, ICellRendererParams } from "ag-grid-community";
import classNames from "classnames";
import { useCallback, useMemo, useRef } from "react";
import Barcode from "react-barcode";
import ProductCartonInput from "./ProductCartonInput";

interface OrderProductProps {
  products: IntegrationProductVo[];
  productUsage: Record<string, number>;
  className?: string;
  productCartons: Record<string, number>;
  changeCartons: (productNo: string, cartons: number) => void;
}

const Columns: ColDef<IntegrationProductVo>[] = [
  {
    headerName: "SKU",
    field: "productNo",
    resizable: true,
    pinned: "left",
    minWidth: 120,
    maxWidth: 240,
    suppressMenu: true,
    rowDrag: false,
    cellRenderer: ({
      data,
    }: ICellRendererParams<IntegrationProductVo, string>) => (
      <span className="text-[14px] text-wrap break-all">{data!.productNo}</span>
    ),
  },
  {
    headerName: "Product",
    resizable: true,
    minWidth: 360,
    suppressMenu: true,
    flex: 1,
    rowDrag: false,
    cellRenderer: ({
      data,
    }: ICellRendererParams<IntegrationProductVo, unknown>) => {
      if (!data) return null;
      return (
        <div className="flex flex-col">
          <div className="flex flex-row items-center gap-2">
            <span className="text-sm font-medium">Name:</span>
            <span className="text-sm text-gray-500">{data.productName}</span>
          </div>
          {data.barcode && (
            <div className="flex flex-row items-center gap-2">
              <Barcode value={data.barcode} displayValue height={30} />
            </div>
          )}
        </div>
      );
    },
  },
  {
    headerName: "Left Quantity",
    field: "quantity",
    resizable: false,
    minWidth: 120,
    maxWidth: 120,
    pinned: "right",
    suppressMenu: true,
    rowDrag: false,
    cellRenderer: ({
      data,
    }: ICellRendererParams<IntegrationProductVo, unknown>) => (
      <span className="text-[16px]">{data!.quantity} pcs</span>
    ),
  },
];

export default function OrderProducts(props: OrderProductProps) {
  const { products, productUsage, className, productCartons, changeCartons } =
    props;
  const tableRef = useRef<BasicTableRef>(null);

  const leftProducts = useMemo(() => {
    return products.map((product) => {
      const used = productUsage[product.productNo] ?? 0;
      return {
        ...product,
        quantity: product.quantity - used,
      };
    });
  }, [products, productUsage]);

  const pageData = useMemo(() => {
    return {
      items: leftProducts,
      total: leftProducts.length,
      page: 1,
      size: leftProducts.length,
      pages: 1,
      sizes: [10, 20, 50, 100],
    };
  }, [leftProducts]);

  // const pageOptions = useMemo(() => {
  //   return Array.from({ length: shippingLabelPageCount }, (_, index) => ({
  //     label: `P ${index + 1}`,
  //     value: index + 1,
  //   }));
  // }, [shippingLabelPageCount]);

  const changeCartonSize = useCallback(
    (productNo: string, cartons: number) => changeCartons(productNo, cartons),
    [changeCartons]
  );

  const actionColumn = useMemo(() => {
    return {
      headerName: "Cartons",
      resizable: false,
      minWidth: 180,
      maxWidth: 180,
      pinned: "right",
      suppressMenu: true,
      rowDrag: false,
      cellRenderer: ({
        data,
      }: ICellRendererParams<IntegrationProductVo, unknown>) => (
        <ProductCartonInput
          productNo={data!.productNo}
          quantity={data!.quantity}
          cartons={productCartons[data!.productNo]}
          onChange={changeCartonSize}
        />
      ),
    } as ColDef<IntegrationProductVo>;
  }, [changeCartonSize, productCartons]);

  // const itemRefs = useRef<Record<string, StupidSelectRef | null>>({});

  // const setItemRef = useCallback(
  //   (productNo: string) => (inst: StupidSelectRef | null) => {
  //     itemRefs.current[productNo] = inst;
  //     inst?.updateSize(productCartons[productNo]);
  //   },
  //   [productCartons]
  // );

  // const skuShppingLabelPagesColumn = useMemo(() => {
  //   return {
  //     headerName: "Shipping Label Pages",
  //     resizable: false,
  //     minWidth: 280,
  //     maxWidth: 280,
  //     suppressMenu: true,
  //     rowDrag: false,
  //     sortable: false,
  //     cellRenderer: ({
  //       data,
  //     }: ICellRendererParams<IntegrationProductVo, unknown>) => {
  //       const value =
  //         productPagesRef.current[data!.productNo] ?? EmptyShippingLabelPages;
  //       return (
  //         <StupidSelect
  //           ref={setItemRef(data!.productNo)}
  //           className="w-full"
  //           productNo={data!.productNo}
  //           onSelectedChange={changeCartonShippingLabelPages}
  //           value={value}
  //         />
  //       );
  //     },
  //   };
  // }, [
  //   changeCartonShippingLabelPages,
  //   setItemRef,
  // ]);

  const columns = useMemo(() => {
    const cols = [...Columns, actionColumn];
    return cols;
  }, [actionColumn]);

  // const getRowHeight = useCallback(
  //   ({ data }: RowHeightParams<IntegrationProductVo>) => {
  //     const productNo = data!.productNo;
  //     const pages =
  //       productShippingLabelPages[productNo] ?? EmptyShippingLabelPages;
  //     let totalRows = 1;
  //     let currentWidth = 0;
  //     pages.forEach((one) => {
  //       const length = String(one).length;
  //       let width = 0;
  //       if (length == 1) {
  //         width += 54;
  //       } else if (length == 2) {
  //         width += 64;
  //       } else if (length == 3) {
  //         width += 72;
  //       }
  //       if (currentWidth + width > 215) {
  //         totalRows += 1;
  //         currentWidth = width;
  //       } else {
  //         currentWidth += width;
  //       }
  //     });
  //     return Math.max(totalRows * 28 + 40, 100);
  //   },
  //   [productShippingLabelPages]
  // );

  // useEffect(() => {
  //   productPagesRef.current = productShippingLabelPages;
  //   //
  //   const currentProduct: Record<number, string> = {};
  //   Object.entries(productShippingLabelPages).forEach(([key, value]) => {
  //     value.forEach((page) => {
  //       currentProduct[page] = key;
  //     });
  //   });
  // }, [productShippingLabelPages]);

  return (
    <BasicTable
      className={classNames("order-table", className)}
      ref={tableRef}
      columns={columns}
      page={pageData}
      loading={false}
      getRowId={(row) => `${row.productNo}`}
      enableRowDetail={true}
      enablePagination={false}
      rowHeight={100}
    />
  );
}

// interface StupidSelectProps {
//   className?: string;
//   productNo: string;
//   value: number[];
//   pageOptions: PageOption[];
//   onSelectedChange: (productNo: string, pages: number[]) => void;
// }

// interface StupidSelectRef {
//   updateSize: (size: number) => void;
//   updateCurrent: (currentProduct: Record<number, string>) => void;
// }

// const EmptyCurrentProduct: Record<number, string> = {};

// const StupidSelect = forwardRef<StupidSelectRef, StupidSelectProps>(
//   (props, ref) => {
//     const { className, productNo, value, onSelectedChange, pageOptions } =
//       props;
//     const { hintPrompt } = useGlobalContext();

//     const [selected, setSelected] = useState<number[]>(
//       value ?? EmptyShippingLabelPages
//     );
//     const [maxLength, setMaxLength] = useState(100);

//     const currentPageMapRef =
//       useRef<Record<number, string>>(EmptyCurrentProduct);

//     const onChange = useCallback(
//       (v: number[]) => {
//         if (v.length > maxLength) {
//           hintPrompt(
//             "error",
//             "Error",
//             `You can only select up to ${maxLength} pages`
//           );
//           return;
//         }
//         v.sort((a, b) => a - b);
//         const invalids = v.filter((page) => {
//           const flag1 = page in currentPageMapRef.current;
//           const flag2 = currentPageMapRef.current[page] !== productNo;
//           return flag1 && flag2;
//         });
//         if (invalids.length > 0) {
//           hintPrompt(
//             "error",
//             "Error",
//             `Page ${invalids.join(", ")} is already assigned to another product`
//           );
//           return;
//         }
//         setSelected(v);
//         onSelectedChange(productNo, v);
//       },
//       [productNo, onSelectedChange, maxLength, hintPrompt]
//     );

//     useImperativeHandle(ref, () => ({
//       updateSize: (size: number) => {
//         if (selected.length > size) {
//           const newValue = [...selected.slice(0, size)];
//           setSelected(newValue);
//           onSelectedChange(productNo, newValue);
//         }
//         setMaxLength(size);
//       },
//       updateCurrent: (currentProduct: Record<number, string>) => {
//         currentPageMapRef.current = currentProduct;
//       },
//     }));

//     return (
//       <Select
//         className={className}
//         value={selected}
//         mode="multiple"
//         maxLength={maxLength}
//         showSearch={false}
//         allowClear
//         options={pageOptions}
//         onChange={onChange}
//         placeholder="Select Shipping Label Pages"
//       />
//     );
//   }
// );
